import { useMutation, useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import {
  GraphQLAPI,
  GraphQLResult,
  parseSearchResults,
  queries,
  typedGqlMutation,
} from "@examspark/api";
import {
  collectAllPages,
  exists,
  getTagValueFromResource,
  SystemTag,
} from "@examspark/common";
import {
  Classroom,
  ClassroomData,
  CreateClassroomInput,
  GenericRelationshipInput,
  ModelResourceConnection,
  ModelStudentConnection,
  ParsedClassroom,
  ParsedSearchableResourceConnectionNew,
  QuerySearchArgs,
  Resource,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  Student,
  UpdateClassroomInput,
  UserClassroom,
  Workspace,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { itemsGql } from "../../hooks/useSearch";
import { useAppContext } from "../../providers/AppContext";

const plannerItemsFields = `
nextToken
items {
  id
  sk
  workspaceId
  description
  resourceId
  resource {
    id
    sk
    type
    name
    createdAt
    url
    tags {
      type
      key
      value
    }
  }
}
`;

/// dateString. The start of a yyyy-mm-dd string. 2024 or 2024-03 or 2024-03-01, etc.
// Consider using the month (2023-01) form of datestring for best cache performance.
export const useQueryListPlannerItems = (
  dateString?: string,
  allowQueryingAllItems?: boolean,
) => {
  const [bonfire] = useAppContext();
  const { data: userClassrooms } = useQueryGetUserClassrooms(
    bonfire.workspaceUser?.id ?? undefined,
    bonfire.user?.id,
  );

  // todo: filter to 'active' classrooms once that means something
  const classroomIds =
    userClassrooms?.map((uc) => uc.classroom?.id).filter(exists) ?? [];

  return useQuery<Resource[]>({
    enabled: !!classroomIds?.length && (!!dateString || allowQueryingAllItems),
    queryKey: ["planner-items", dateString],
    queryFn: async () => {
      const items: Resource[] = [];

      await Promise.all(
        classroomIds.map(async (classroomId) => {
          const results = await collectAllPages(async (nextToken) => {
            const result = (await GraphQLAPI.query({
              query: queries.resource.listResources({
                label: "ListClassroomUnitPlannerItems",
                fields: plannerItemsFields,
              }),
              variables: {
                id: classroomId,
                sk: {
                  beginsWith: `classroom-planner-item:${dateString ?? ""}`,
                },
                nextToken,
              },
            })) as GraphQLResult<{ listResources: ModelResourceConnection }>;

            return result.data?.listResources;
          });

          items.push(...results.flatMap((r) => r.items ?? []).filter(exists));
        }),
      );

      return items;
    },
  });
};

export const useQueryListClassroomUnitPlannerItems = (
  classroomId?: string | null,
  unitId?: string,
) => {
  return useQuery<Resource[]>({
    enabled: !!classroomId && !!unitId,
    queryKey: ["classroom", classroomId, "planner-items", unitId],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.resource.listResources({
          label: "ListClassroomUnitPlannerItems",
          fields: `
          items {
            id
            sk
            workspaceId
            description
            resourceId
            resource {
              id
              sk
              name
              createdAt
              url
            }
          }
          `,
        }),
        variables: {
          id: classroomId,
          sk: {
            beginsWith: `classroom-unit-planner-item:${unitId}`,
          },
        },
      })) as any;

      return result.data.listResources.items;
    },
  });
};

export const useQueryListClassroomPlannerItems = (
  classroomId?: string | null,
) => {
  return useQuery<Resource[]>({
    enabled: !!classroomId,
    queryKey: ["classroom", classroomId, "planner-items"],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.resource.listResources({
          label: "ListClassroomUnitPlannerItems",
          fields: `
          items {
            id
            sk
            workspaceId
            description
            resourceId
            resource {
              id
              sk
              name
              createdAt
              url
            }
          }
          `,
        }),
        variables: {
          id: classroomId,
          sk: {
            beginsWith: `classroom-planner-item:`,
          },
        },
      })) as any;

      return result.data.listResources.items;
    },
  });
};

export const useQuerySearchClassroomResources = (params: {
  classroomId?: string;
  unitId?: string;
}) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["classroom", params.classroomId, "resources", params.unitId],
    enabled: !!params.classroomId,
    queryFn: async () => {
      const tags: SearchableResourceFilterInput["and"] = [
        {
          tags: {
            eq: `classroom-id#${params.classroomId}`,
          },
        },
      ];

      if (params.unitId) {
        tags.push({
          tags: {
            eq: `unit-id#${params.unitId}`,
          },
        });
      }

      const result = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query<{
          search: SearchableResourceConnectionNew;
        }>({
          query: queries.search.search({
            label: "SearchClassroomResources",
            fields: `
                id
                sk
                type
                workspaceId
                name
                description
                data
                imageUrl
                url
                file {
                  bucket
                  region
                  key
                }
                workspace {
                  id
                  name
                }
              `,
          }),
          variables: {
            filter: {
              sk: {
                eq: "resource#v0",
              },
              and: tags,
              or: [
                {
                  type: {
                    eq: "EXTERNAL_LINK",
                  },
                },
                {
                  type: {
                    eq: "DOCUMENT",
                  },
                },
                {
                  type: {
                    eq: "IMAGE",
                  },
                },
                {
                  type: {
                    eq: "FILE",
                  },
                },
              ],
            },
            sort: [
              {
                field: "updatedAt",
                direction: "desc",
              },
            ],
            authorizationContext,
            nextToken,
          },
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        const next = produce(result.data?.search, (draft) => {
          if (!draft) return;
          if (typeof draft.aggregations === "string") {
            draft.aggregations = JSON.parse(draft.aggregations);
          }
        }) as ParsedSearchableResourceConnectionNew;

        return next;
      });

      return result.flatMap((r) => r.items ?? []);
    },
  });
};

/** List classrooms. By default, filter out any `isArchived` classrooms. */
export const useQueryGetUserClassrooms = (
  workspaceId?: string,
  userId?: string | null,
  // @todo move this into `options` next time its convenient in a refactor
  includeAll?: boolean,
  options?: Partial<{
    filter: Partial<{
      status: "active" | "archived";
      ltiContextId: string;
    }>;
  }>,
) => {
  const [state] = useAppContext();

  return useQuery({
    enabled: !!userId && !!workspaceId,
    queryKey: ["classrooms", userId, workspaceId, includeAll, options],
    queryFn: async () => {
      if (!userId) return;
      if (!workspaceId) return;

      const pages = await collectAllPages(async (nextToken) => {
        const result = await GraphQLAPI.query<{
          listUserClassrooms: ModelResourceConnection;
        }>({
          query: queries.classroom.listUserClassrooms,
          variables: {
            input: {
              userId,
              includeAll: includeAll,
            },
            authorizationContext: {
              workspaceId,
              appId: state.app?.id,
              userId: state.user?.id,
            },
            nextToken,
          },
        });

        return result.data?.listUserClassrooms;
      });

      const results = pages
        .flatMap((r) => r.items ?? [])
        .filter(exists) as UserClassroom[];

      return results
        .filter((uc: UserClassroom) => {
          if (options?.filter?.status == null) {
            return !uc.classroom?.isArchived;
          }
          if (options?.filter.status === "active") {
            return !uc.classroom?.isArchived;
          }
          if (options?.filter.status === "archived") {
            return !!uc.classroom?.isArchived;
          }
          return true;
        })
        .filter((uc) => {
          if (options?.filter?.ltiContextId) {
            const ltiContextId = getTagValueFromResource(
              uc.classroom,
              SystemTag.LtiContextId,
            );
            return ltiContextId === options.filter.ltiContextId;
          }
          return true;
        });
    },
  });
};

export const useQueryGetClassroom = (classroomId?: string | null) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery<ParsedClassroom>({
    enabled: !!classroomId,
    queryKey: ["classroom", classroomId],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.classroom.getClassroom,
        variables: {
          input: {
            id: classroomId,
            sk: "classroom",
          },
          authorizationContext,
        },
      })) as any;

      return produce<ParsedClassroom>(
        result.data.getClassroom,
        (draft: Classroom) => {
          // todo: use deserializer from api package?
          if (draft.data && typeof draft.data === "string") {
            draft.data = JSON.parse(draft.data) as ClassroomData;
          }
          if (draft.unit?.data && typeof draft.unit.data === "string") {
            draft.unit.data = JSON.parse(draft.unit.data);
          }
          for (const unit of draft.course?.units ?? []) {
            if (typeof unit.unit?.data === "string") {
              unit.unit.data = JSON.parse(unit.unit.data);
            }
          }
        },
      );
    },
  });
};

export const useQueryGetStudent = (
  classroomId?: string | null,
  studentId?: string | null,
) => {
  return useQuery<Student>({
    enabled: !!classroomId && !!studentId,
    queryKey: ["student", classroomId, studentId],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.classroom.getStudent,
        variables: {
          input: {
            id: classroomId,
            sk: `student#${studentId}`,
          },
        },
      })) as any;

      return (result.data.getStudent as Student) || null;
    },
  });
};

export const useQueryGetClassroomStudents = (
  workspaceId?: string,
  classroomId?: string | null,
) => {
  return useQuery<Student[]>({
    enabled: !!classroomId && !!workspaceId,
    queryKey: ["classroom-students", workspaceId, classroomId],
    queryFn: async () => {
      const pages = await collectAllPages(async (nextToken) => {
        const result = await GraphQLAPI.query<{
          listClassroomStudents: ModelStudentConnection;
        }>({
          query: queries.classroom.listClassroomStudents,
          variables: {
            input: {
              id: classroomId,
              sk: {
                beginsWith: "#",
              },
              workspaceId: workspaceId,
              nextToken,
            },
          },
        });

        return result.data?.listClassroomStudents;
      });

      return pages.flatMap((r) => r.items ?? []).filter(exists);
    },
  });
};

export const useQueryGetClassroomAverageByUnit = (classroomId?: string) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery({
    enabled: !!classroomId,
    queryKey: ["classroom-average-by-unit", classroomId],
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        limit: 0,
        filter: {
          sk: {
            wildcard: "exam-response:*",
          },
          tags: {
            eq: `classroom-id#${classroomId}`,
          },
        },
        aggsNew: [
          {
            name: "tagsUnit",
            type: "nested",
            path: "tags",
            aggs: [
              {
                name: "values",
                type: "filter",
                query: JSON.stringify({
                  terms: {
                    "tags.key.keyword": ["unit-id"],
                  },
                }),
                aggs: [
                  {
                    name: "values",
                    type: "terms",
                    field: "tags.value",
                    aggs: [
                      {
                        name: "parent_avg_score",
                        type: "reverse_nested",
                        aggs: [
                          {
                            name: "avg_score",
                            type: "avg",
                            field: "scoreActual",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      };

      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "GetClassroomAverageByUnit",
          fields: `id sk name idInc createdAt updatedAt subject`,
        }),
        variables,
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      const scoresByUnit: Record<string, number> = {};

      const aggs = JSON.parse(
        result.data?.search.aggregations as string,
      ) as any;

      for (const unitBucket of aggs?.tagsUnit?.values?.values?.buckets ?? []) {
        scoresByUnit[unitBucket.key] =
          unitBucket.parent_avg_score.avg_score.value;
      }

      return scoresByUnit;
    },
  });
};

export const useQueryGetClassroomScoresByStudent = (classroomId: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["classroom-average-by-student", classroomId],
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        limit: 0,
        filter: {
          sk: {
            wildcard: "exam-response:*",
          },
          tags: {
            eq: `classroom-id#${classroomId}`,
          },
        },
        aggsNew: [
          {
            name: "tagsUnit",
            type: "nested",
            path: "tags",
            aggs: [
              {
                name: "values",
                type: "filter",
                query: JSON.stringify({
                  terms: {
                    "tags.key.keyword": ["classroom-student-id"],
                  },
                }),
                aggs: [
                  {
                    name: "values",
                    type: "terms",
                    field: "tags.value",
                    aggs: [
                      {
                        name: "parent_avg_score",
                        type: "reverse_nested",
                        aggs: [
                          {
                            name: "avg_score",
                            type: "avg",
                            field: "scoreActual",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      };

      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "GetClassroomAverageByStudent",
          fields: `id sk name idInc createdAt updatedAt subject`,
        }),
        variables,
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      const scoresByStudent: Record<string, number> = {};

      const aggs = JSON.parse(
        result.data?.search.aggregations as string,
      ) as any;

      for (const studentBucket of aggs?.tagsUnit?.values?.values?.buckets ??
        []) {
        scoresByStudent[studentBucket.key] =
          studentBucket.parent_avg_score.avg_score.value;
      }

      return scoresByStudent;
    },
  });
};

export const useMutationCreateClassroom = () => {
  return useMutation({
    mutationFn: async (input: CreateClassroomInput) => {
      return await GraphQLAPI.query({
        query: queries.classroom.createClassroom,
        variables: {
          input,
        },
      });
    },
  });
};

export const useMutationUpdateClassroom = () => {
  return useMutation({
    mutationFn: async (input: UpdateClassroomInput) => {
      return await GraphQLAPI.query({
        query: queries.classroom.updateClassroom,
        variables: {
          input,
        },
      });
    },
  });
};

export const useMutationCreateUserClassroom = () => {
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      classroomId: string;
      workspaceId: string;
    }) => {
      return await GraphQLAPI.query({
        query: queries.classroom.createUserClassroom,
        variables: {
          input: {
            left: {
              id: input.userId,
              sk: "user",
              typename: "User",
            },
            right: {
              id: input.classroomId,
              sk: "classroom",
              typename: "Classroom",
            },
            workspaceId: input.workspaceId,
          } as GenericRelationshipInput,
        },
      });
    },
  });
};

export const useMutationArchiveClassroomAction = () => {
  return useMutation({
    mutationFn: async (input: { classroomId: string; isArchived: boolean }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation ArchiveClassroom($input: ControllerInput!) {
            archiveClassroomAction(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            action: "archiveClassroom",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.archiveClassroomAction;
    },
  });
};

export const useMutationResetClassroomAction = () => {
  return useMutation({
    mutationFn: async (input: { classroomId: string }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation ResetClassroom($input: ControllerInput!) {
            resetClassroomAction(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            action: "resetClassroom",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.resetClassroomAction;
    },
  });
};

export const useMutationDeleteClassroomAction = () => {
  return useMutation({
    mutationFn: async (input: { userId: string; classroomId: string }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation DeleteClassroom($input: ControllerInput!) {
            deleteClassroomAction(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            action: "deleteClassroom",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.deleteClassroomAction;
    },
  });
};

export const useMutationImportClassroomRoster = () => {
  return useMutation({
    mutationFn: async (input: {
      workspaceId: string;
      classroomId: string;
      key: string;
    }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation ImportClassroomRoster($input: ControllerInput!) {
            importClassroomRosterAction(input: $input)
          }
        `,
        variables: {
          input: {
            action: "importClassroomRoster",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.importClassroomRoster;
    },
  });
};

export const useMutationExportClassroomRoster = () => {
  return useMutation({
    mutationFn: async (input: { workspaceId: string; classroomId: string }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation ExportClassroomRoster($input: ControllerInput!) {
            exportClassroomRosterAction(input: $input)
          }
        `,
        variables: {
          input: {
            action: "exportClassroomRoster",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.exportClassroomRosterAction;
    },
  });
};

export const useMutationCreateClassroomResource = () => {
  return useMutation({
    mutationFn: async (input: Resource) => {
      // increment workspace number
      const incrementResult = (await GraphQLAPI.query<{
        incrementWorkspaceCounter: Workspace;
      }>({
        query: queries.workspace.incrementWorkspaceNumber,
        variables: {
          input: {
            id: input.workspaceId,
            sk: "workspace",
          },
        },
      })) as GraphQLResult<{
        incrementWorkspaceCounter: Workspace;
      }>;

      if (!incrementResult.data?.incrementWorkspaceCounter.counter) {
        throw new Error("Unable to genereate new workspace number.");
      }

      const idInc =
        incrementResult.data?.incrementWorkspaceCounter.counter.toString();

      // create resource
      const createResult = (await GraphQLAPI.query<{
        createResource: Resource;
      }>({
        query: queries.resource.createResource(),
        variables: {
          input: {
            ...input,
            idInc,
          },
        },
      })) as GraphQLResult<{
        createResource: Resource;
      }>;

      console.log("result", createResult);
    },
  });
};

export const useMutationDeleteClassroomResource = () => {
  const authorizationContext = useAuthorizationContext();
  return useMutation({
    mutationFn: async (input: Resource) => {
      if (!input.workspaceId) {
        throw new Error("Cannot delete resource without workspaceId");
      }
      const deleteResource = await typedGqlMutation(
        queries.classroom.deleteClassroomResource,
      )({
        input: {
          id: input.id,
          sk: input.sk,
          workspaceId: input.workspaceId,
        },
        authorizationContext,
      });

      if (deleteResource.errors) {
        throw new Error(
          deleteResource.errors.map((e) => e.message).join("\n\n"),
        );
      }
      return deleteResource.data?.deleteClassroomResource;
    },
  });
};

export const useMutationUpdateClassroomSettings = () => {
  return useMutation({
    mutationFn: async (classroom: Classroom) => {
      const res = await typedGqlMutation(queries.classroom.updateClassroom)({
        input: {
          id: classroom.id,
          sk: classroom.sk,
          workspaceId: classroom.workspaceId,
          name: classroom.name,
          calendarId: classroom.calendarId,
          sectionNumber: classroom.sectionNumber,
          room: classroom.room,
        },
      });

      return res;
    },
  });
};

export const useQueryGetClassroomAssignmentCountByUnit = (
  classroomId?: string,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery<Record<string, number>>({
    enabled: !!classroomId,
    queryKey: ["classroom-assignment-count-by-unit", classroomId],
    queryFn: async () => {
      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "GetClassroomAssignmentCountByUnit",
          fields: itemsGql,
        }),
        variables: {
          limit: 0,
          filter: {
            sk: {
              eq: "exam-config",
            },
            tags: {
              eq: `classroom-id#${classroomId}`,
            },
            isDeleted: {
              ne: true,
            },
          },
          aggsNew: [
            {
              name: "tagsUnitId",
              type: "nested",
              path: "tags",
              aggs: [
                {
                  name: "values",
                  type: "filter",
                  query: JSON.stringify({
                    terms: {
                      "tags.key.keyword": ["classroom-unit-id"],
                    },
                  }),
                  aggs: [
                    {
                      name: "values",
                      type: "terms",
                      field: "tags.value",
                    },
                  ],
                },
              ],
            },
          ],
          authorizationContext,
        },
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      const parsed = parseSearchResults(result.data?.search);
      console.log("parsed", parsed);
      const countsByUnitId =
        parsed.aggregations?.tagsUnitId.values.values.buckets.reduce(
          (
            acc: Record<string, number>,
            bucket: { key: string; doc_count: number },
          ) => {
            acc[bucket.key] = bucket.doc_count;
            return acc;
          },
          {},
        );

      console.log("countsByUnitId", countsByUnitId);
      return countsByUnitId ?? ({} as Record<string, number>);
    },
  });
};
